<template>
  <div class="content-calendar-new-page">
    <!--<StepsProgressBar class="content-calendar-new-page__progressbar" :currentStep="activeStep" :numberOfSteps="totalNumberOfSteps" :fontSize="19" color="teal" />-->

    <StepUploadContent
      :key="uploadStepKey"
      class="content-calendar-new-page__step-container"
      :customerId="customerId"
      :galleryGroups="allPhotoGalleryGroups"
      :locations="savedLocations"
      :minimumAssetsForInitialPostGen="minimumAssetsForInitialPostGen"
      :processedCommunityUploadAssets="processedCommunityUploadAssets"
      @update:galleryGroups="updatedGalleryGroupsList => (allPhotoGalleryGroups = updatedGalleryGroupsList)"
      :isLoading="isLoadingPhotoGalleryGroups"
      :isBackVisible="onboarding === 'UploadPhotos'"
      :isFirstPostsFlow="isFirstPostsFlow"
      @goBack="async () => await router.push({ name: 'AtlasOnboardingLocations', params: { customerId } })"
      @proceed="handleProceed"
    />

    <SuperUserActionPanel class="content-calendar-new-page__super-panel">
      <div class="seed-photos">
        <SrpButton tag="button" type="button" color="gray" fill="outlined" size="tiny" :isDisabled="isSeedingPhotos" @click="seedPhotos">
          {{ isSeedingPhotos ? "Seeding photos..." : "Seed test photos" }}
        </SrpButton>
        <div v-if="seedPhotosError" class="seed-photos__error">{{ seedPhotosError }}</div>
        <div v-if="seedPhotosResult" class="seed-photos__result">
          <div>Added {{ seedPhotosResult.albumCount }} albums ({{ seedPhotosResult.photoCount }} photos).</div>
          <ul v-if="seedPhotosResult.notes.length || seedPhotosResult.warnings.length">
            <li v-for="line in [...seedPhotosResult.notes, ...seedPhotosResult.warnings]" :key="line">{{ line }}</li>
          </ul>
        </div>
      </div>
    </SuperUserActionPanel>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useHead } from "@unhead/vue";
import ContentCalendarRepo from "@repos/ContentCalendarRepo";
import SocialLocationsRepo from "@repos/SocialLocationsRepo";
import SocialMediaSettingsRepo from "@repos/SocialMediaSettingsRepo";
import TestDataRepo from "@repos/TestDataRepo";
import { getGlobalRemoteLogger } from "@helpers/RemoteLogger";
import { MetricSender } from "@/helpers/MetricSender";
import AdminContext from "@logic/AdminContext";

// Components
// steps
import StepUploadContent from "./steps/StepUploadContent.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SuperUserActionPanel from "@components/SuperUserActionPanel.vue";

// Types
import { PhotoGalleryCustomerData } from "@contracts/photosPageData";
import { SocialOperationType } from "@contracts/contentCalendar";
import type { AtlasOnboardingStep } from "@contracts/socialMediaSettings";
import type { LocationData } from "@contracts/socialLocations";
import type { AtlasSeedResult } from "@contracts/testData";

// Router
const route = useRoute();
const router = useRouter();
const remoteLogger = getGlobalRemoteLogger();

defineOptions({
  name: "CalendarUploadContent",
});

// Normalize route params ------------------------------------------------------
const customerId = computed<string>(() => (Array.isArray(route.params.customerId) ? route.params.customerId[0] : (route.params.customerId as string)));

const pageTitle = ref("Upload Photos");
useHead({ title: () => pageTitle.value });

// Load or generate photo galleries ===========================================
const allPhotoGalleryGroups = ref<Array<PhotoGalleryCustomerData>>([]);
const savedLocations = ref<Array<LocationData>>([]);
const minimumAssetsForInitialPostGen = ref<number>(0);
const processedCommunityUploadAssets = ref<number>(0);

const isLoadingPhotoGalleryGroups = ref<boolean>(true);

async function loadSavedLocations(): Promise<Array<LocationData>> {
  try {
    return await SocialLocationsRepo.list(customerId.value, false, false);
  } catch (error) {
    // Non-blocking: the existing upload groups still work without the location rows.
    remoteLogger?.error(`UploadContentPage.loadSavedLocations failed. customerId=${customerId.value} error=${error}`);
    return [];
  }
}

// Onboarding and first posts =================================================
// At the UploadPhotos step this page is Atlas onboarding's upload step, so it offers a way back to the locations step.
// Only at that step: customers at the earlier steps haven't been through the locations page (ex. existing customers the
// backfill started at NeedsLocations), so Back would send them somewhere they never were.
// Missing/null means onboarding was never started, which is treated like Done.
const onboarding = ref<AtlasOnboardingStep | null>(null);
// Until the calendar has generated its first posts, Proceed opens the "Create your first posts" modal.
// Same rule as the server (CalendarSetupSharedLogic.KickoffInitialContentGenIfNeeded).
const isFirstPostsFlow = ref<boolean>(false);

async function loadCalendarState(): Promise<{ onboarding: AtlasOnboardingStep | null; isFirstPostsFlow: boolean }> {
  try {
    const settings = (await SocialMediaSettingsRepo.getSettings(customerId.value)).settings;
    const currentOperation = settings?.currentOperation;
    return {
      onboarding: settings?.onboarding ?? null,
      isFirstPostsFlow: !settings?.firstPostGenWeek && (currentOperation === SocialOperationType.NeedInitialContent || currentOperation === SocialOperationType.LackingContentVariety),
    };
  } catch (error) {
    // Non-blocking: without the settings we just don't show Back, and Proceed goes straight to Posts.
    remoteLogger?.error(`UploadContentPage.loadCalendarState failed. customerId=${customerId.value} error=${error}`);
    return { onboarding: null, isFirstPostsFlow: false };
  }
}

async function handleProceed() {
  // A calendar that already has posts never sees the first posts modal, so Proceed is the end of its onboarding.
  // (In the first posts flow, starting the posts ends it on the server.) Not blocking: Posts works the same without it.
  if (onboarding.value === "UploadPhotos" && !isFirstPostsFlow.value) {
    try {
      await SocialMediaSettingsRepo.completeOnboardingStep(customerId.value, "UploadPhotos");
    } catch (error) {
      remoteLogger?.error(`UploadContentPage.handleProceed: completing the step failed. customerId=${customerId.value} error=${error}`);
    }
  }
  await router.push({ name: "CalendarPosts", params: { customerId: customerId.value } });
}

// Superuser: seed test photos ================================================
// Same photos step as /super/test-data (AtlasTestCustomerSeeder.SeedPhotosAsync). The server refuses customers that aren't test customers.
const isSeedingPhotos = ref(false);
// The upload list copies the albums when it mounts and doesn't watch for new ones, so a new key re-mounts it with the seeded albums.
const uploadStepKey = ref(0);
const seedPhotosResult = ref<AtlasSeedResult | null>(null);
const seedPhotosError = ref("");

async function seedPhotos() {
  if (isSeedingPhotos.value) return;
  isSeedingPhotos.value = true;
  seedPhotosError.value = "";
  seedPhotosResult.value = null;
  try {
    seedPhotosResult.value = await TestDataRepo.seedPhotos(customerId.value);
    // Reload so the new albums show on their rows without a page refresh
    if (seedPhotosResult.value.albumCount > 0) {
      allPhotoGalleryGroups.value = await ContentCalendarRepo.loadUploadGroups(customerId.value);
      uploadStepKey.value++;
    }
  } catch (error: any) {
    const data = error?.response?.data;
    seedPhotosError.value = typeof data === "string" && data ? data : error?.message ?? "Seeding photos failed.";
  } finally {
    isSeedingPhotos.value = false;
  }
}

onMounted(async () => {
  const org = await AdminContext.setOrgInContextFromCustomerId(customerId.value);
  if (org?.name) pageTitle.value = `Upload Photos - ${org.name}`;
  isLoadingPhotoGalleryGroups.value = true;
  // Load the upload groups (they get created on setup now)
  const [uploadGroups, uploadRequirements, locations, calendarState] = await Promise.all([
    ContentCalendarRepo.loadUploadGroups(customerId.value),
    ContentCalendarRepo.loadUploadRequirements(customerId.value),
    loadSavedLocations(),
    loadCalendarState(),
  ]);
  onboarding.value = calendarState.onboarding;
  isFirstPostsFlow.value = calendarState.isFirstPostsFlow;
  allPhotoGalleryGroups.value = uploadGroups;
  savedLocations.value = locations;
  minimumAssetsForInitialPostGen.value = uploadRequirements.minimumAssetsForInitialPostGen;
  processedCommunityUploadAssets.value = uploadRequirements.processedCommunityUploadAssets ?? 0;

  isLoadingPhotoGalleryGroups.value = false;

  MetricSender.sendMetric("Cust-Calendar-Uploads-PageView", { customerId: customerId.value });
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";

// Content calendar new page ==================================================
.content-calendar-new-page {
  width: $desktop-wide-right-col-width;
  margin: 0 auto;

  &__progressbar {
    margin-bottom: 40px;
  }

  // Compact and out of the way: a small box at the right edge, not a full-width bar.
  &__super-panel {
    width: fit-content;
    max-width: 360px;
    margin: 16px 0 0 auto;
  }
}

.seed-photos {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: flex-end;
  text-align: right;
  font-size: 0.9em;

  &__error {
    flex-basis: 100%;
    color: #b42318;
  }

  &__result {
    flex-basis: 100%;

    ul {
      margin: 4px 0 0;
      padding: 0;
      list-style: none;
    }
  }
}
// desktop wide -----------------------
@media (min-width: $desktop-wide-min-width) {
}
// desktop ----------------------------
@media (min-width: $desktop-min-width) and (max-width: $desktop-max-width) {
  .content-calendar-new-page {
    width: $desktop-right-col-width;
  }
}
// laptop -----------------------------
@media (min-width: $laptop-min-width) and (max-width: $laptop-max-width) {
  .content-calendar-new-page {
    width: $laptop-right-col-width;
  }
}
// tablet large -----------------------
@media (min-width: $tablet-large-min-width) and (max-width: $tablet-large-max-width) {
  .content-calendar-new-page {
    width: $tablet-large-content-width;
  }
}
// tablet -----------------------------
@media (min-width: $tablet-min-width) and (max-width: $tablet-max-width) {
  .content-calendar-new-page {
    width: $tablet-content-width;
  }
}
// mobile -----------------------------
@media (max-width: $mobile-max-width) {
  .content-calendar-new-page {
    width: $mobile-content-width;
  }
}
</style>
